import { 
  Thumbnail, 
  Text, 
  BlockStack,
  Button,
} from "@medentic/polaris-react";
import { useCountries } from "@shared/hooks/common/useCountries";
import { useState, useCallback, useEffect } from "react";
import { CountrySelectionModal } from "./CountrySelectionModal";

interface CountryOption {
  label: string;
  value: string;
  flag: string;
  phoneCode: string;
}

interface CountryComboboxProps {
  value: string | null;
  onChange: (value: string) => void;
  countryISO3Codes?: string[];
  label?: string;
  labelHidden?: boolean;
}

export function CountryCombobox({
  value,
  onChange,
  countryISO3Codes,
  label = "",
  labelHidden = true,
}: CountryComboboxProps) {
  const { countries } = useCountries();
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // If countryISO3Codes is provided, filter to only those countries
  // Otherwise, show all countries from API
  const filteredCountries = countryISO3Codes 
    ? countries.filter(country => {
        // Make comparison case-insensitive
        return countryISO3Codes.some(code => 
          code.toLowerCase() === country.iso3.toLowerCase()
        );
      })
    : countries;

  const options: CountryOption[] = filteredCountries.map(country => ({
    label: country.name,
    value: country.iso3,
    flag: country.svg,
    phoneCode: country.phoneCountryCode,
  }));

  // Auto-select first country if no country is selected
  useEffect(() => {
    if (options.length > 0 && (!value || !options.find(option => option.value === value))) {
      onChange(options[0].value);
    }
  }, [options, value, onChange]);

  // Find selected country for display
  const selectedCountry = options.find(option => option.value === value);

  const handleSelect = useCallback((selectedValue: string) => {
    onChange(selectedValue);
    setIsModalOpen(false);
  }, [onChange]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // Clickable button that shows the selected country flag
  const countryButton = (
    <Button
      onClick={() => setIsModalOpen(true)}
      icon={selectedCountry && (
        <Thumbnail 
          source={selectedCountry.flag} 
          alt={selectedCountry.label}
          size="extraSmall"
        />
      )}
      variant="tertiary"
      size="slim"
      accessibilityLabel={selectedCountry ? `Selected country: ${selectedCountry.label}` : "Select country"}
    />
  );

  return (
    <BlockStack gap="200">
      {!labelHidden && label && (
        <Text variant="bodyMd" as="p">
          {label}
        </Text>
      )}
      {countryButton}
      
      <CountrySelectionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSelect={handleSelect}
        selectedValue={value}
        countryOptions={options}
      />
    </BlockStack>
  );
} 